import { buttonVariants } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Skeleton } from "@/components/ui/feedback";
import { PAY_COPY } from "./copy";
import { useReceipt } from "./use-pay";

/**
 * The receipt.
 *
 * Requested only once the payment is final, which is decided by the status the
 * service reported — not by a timer and not by the page. While the payment is
 * still confirming or held for review, this card says when the receipt will
 * appear and makes no request, so there is no receipt URL to guess at and no
 * request for a document that does not exist yet.
 *
 * The download target comes from the response and is dropped by the mapper
 * unless it is a same-origin path, so this button can only ever link back to
 * the issuer's own payment service.
 */
export function ReceiptPanel({ token, final }: { token: string; final: boolean }) {
  const { state } = useReceipt(token, final);

  return (
    <Card>
      <CardBody>
        <CardTitle>{PAY_COPY.receiptHeading}</CardTitle>

        {!final && <p className="mt-2 text-sm text-muted">{PAY_COPY.receiptPending}</p>}

        {final && state.kind === "loading" && (
          <div className="mt-3">
            <Skeleton className="h-9 w-40" />
          </div>
        )}

        {final && state.kind === "ready" && state.receipt.url && (
          <div className="mt-3">
            {/* An anchor with the button's own classes, the pattern the
                treasury and approval pages use for a link that must stay a
                link: `download` keeps the browser's own download behaviour and
                the wallet never holds the document in memory. The target is a
                same-origin path (`mapReceipt` drops anything else). */}
            <a href={state.receipt.url} download className={buttonVariants({ variant: "secondary", size: "md" })}>
              {PAY_COPY.receiptDownload}
            </a>
          </div>
        )}

        {final && state.kind === "unavailable" && <p className="mt-2 text-sm text-muted">{PAY_COPY.receiptUnavailable}</p>}

        {final && state.kind === "error" && (
          <div role="alert" className="mt-3 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">
            {state.error.reasonCode}
            <SupportDetails reference={state.error.supportReference} capability={state.error.capability} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}
